class ColorSwatches extends HTMLElement {
  constructor() {
    super();
    this.swatches = this.querySelectorAll('.variant-option__swatch');
    this.currentProductId = this.dataset.productId;
    this.currentColor = this.dataset.currentColor ? this.dataset.currentColor.toLowerCase().trim() : null;
    this.colorMapping = this.dataset.colorMapping ? this.dataset.colorMapping.split(',').map(c => c.trim().toLowerCase()) : [];
    
    // Initialize
    this.initializeSwatches();
    this.setupEventListeners();
  }

  initializeSwatches() {
    // Set initial active state based on current product color
    if (this.currentColor) {
      this.swatches.forEach(swatch => {
        const colorValue = swatch.dataset.colorName || '';
        if (colorValue.toLowerCase() === this.currentColor) {
          swatch.classList.add('is-active');
          swatch.querySelector('input[type="radio"]').checked = true;
        }
      });
    } else if (this.swatches.length > 0) {
      // If no color is set, activate the first swatch
      this.swatches[0].classList.add('is-active');
      this.swatches[0].querySelector('input[type="radio"]').checked = true;
    }
  }

  setupEventListeners() {
    this.swatches.forEach(swatch => {
      const input = swatch.querySelector('input[type="radio"]');
      const label = swatch.querySelector('label');
      const colorValue = swatch.dataset.colorName || '';
      const mediaId = label?.dataset?.mediaId;
      
      // Hover handler - preview image
      swatch.addEventListener('mouseenter', () => {
        if (mediaId) {
          this.previewImage(mediaId);
        }
      });
      
      swatch.addEventListener('mouseleave', () => {
        // Reset to active swatch image
        const activeSwatch = this.querySelector('.variant-option__swatch.is-active');
        const activeMediaId = activeSwatch?.querySelector('label')?.dataset?.mediaId;
        if (activeMediaId) {
          this.previewImage(activeMediaId);
        }
      });
      
      // Click handler - update image only, no navigation
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Don't do anything if this swatch is already active or disabled
        if (swatch.classList.contains('is-active') || swatch.classList.contains('is-disabled')) {
          return;
        }
        
        // Update active state
        this.swatches.forEach(s => s.classList.remove('is-active'));
        swatch.classList.add('is-active');
        
        // Update the radio input
        input.checked = true;
        
        // Update the image
        if (mediaId) {
          this.updateImage(mediaId);
        }
        
        // Dispatch change event for the input
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }
  
  previewImage(mediaId) {
    const card = this.closest('.product-card');
    if (!card) return;
    
    const targetMedia = card.querySelector(`[data-media-id="${mediaId}"]`);
    if (targetMedia) {
      const img = targetMedia.querySelector('img');
      if (img) {
        const mainImg = card.querySelector('.product-card img');
        if (mainImg && !mainImg.dataset.originalSrc) {
          mainImg.dataset.originalSrc = mainImg.src;
        }
        if (mainImg) {
          mainImg.src = img.src;
        }
      }
    }
  }
  
  updateImage(mediaId) {
    const card = this.closest('.product-card');
    if (!card) return;
    
    const targetMedia = card.querySelector(`[data-media-id="${mediaId}"]`);
    if (targetMedia) {
      const img = targetMedia.querySelector('img');
      if (img) {
        const mainImg = card.querySelector('.product-card img');
        if (mainImg) {
          mainImg.src = img.src;
          mainImg.dataset.originalSrc = img.src;
        }
      }
    }
  }
  
  async handleColorChange(selectedColor) {
    try {
      // Get the current URL and remove any existing variant parameters
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('variant');
      
      // Find the product with the selected color
      const response = await fetch(`${currentUrl}?view=color-variants&color=${encodeURIComponent(selectedColor)}`);
      const html = await response.text();
      
      // Parse the response to find the variant ID
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const variantId = doc.querySelector(`[data-color-name="${selectedColor.toLowerCase()}"]`)?.dataset?.variantId;
      
      if (variantId) {
        // Update URL with the new variant
        currentUrl.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', currentUrl.toString());
        
        // Update the page with the new variant
        this.updateProductVariant(variantId);
      }
    } catch (error) {
      console.error('Error handling color change:', error);
    }
  }
  
  async updateProductVariant(variantId) {
    try {
      // Fetch the product data for the selected variant
      const response = await fetch(`/products/${this.currentProductId}?variant=${variantId}&section_id=main-product`);
      const html = await response.text();
      
      // Parse the response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Update the product form
      const productForm = document.querySelector('product-form');
      if (productForm) {
        const newForm = doc.querySelector('product-form');
        if (newForm) productForm.innerHTML = newForm.innerHTML;
      }
      
      // Update the price
      const priceElement = document.querySelector('product-price');
      if (priceElement) {
        const newPrice = doc.querySelector('product-price');
        if (newPrice) priceElement.outerHTML = newPrice.outerHTML;
      }
      
      // Update the media gallery
      const mediaGallery = document.querySelector('media-gallery');
      if (mediaGallery) {
        const newGallery = doc.querySelector('media-gallery');
        if (newGallery) mediaGallery.outerHTML = newGallery.outerHTML;
      }
      
      // Dispatch an event to let other components know the variant has changed
      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: { variantId }
      }));
      
    } catch (error) {
      console.error('Error updating product variant:', error);
    }
  }
}

// Register the custom element
if (!customElements.get('color-swatches')) {
  customElements.define('color-swatches', ColorSwatches);
}
